import { CloseIcon } from '@chakra-ui/icons';
import {
    Badge,
    HStack,
    IconButton,
    Modal,
    ModalOverlay,
    Stack,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import {
    Button,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Text,
} from 'components/v4';
import walkthroughIds from '../../../accounts/admins/walkthroughIds';

import { useTenantDomainForm } from './useTenantDomainForm';
import useAddSSOProvider from './queries/useAddSSOProvider';

import { BUTTON_WIDTH } from './constants';

import MicrosoftProviderPreview from './components/MicrosoftProviderPreview';
import TenantDomainFormBody from './components/TenantDomainFormBody';

export default function AddProvider({ isOpen, onClose }) {
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);
    const { ufBackBtn, ufSubmitBtn } = walkthroughIds.adminSSO;

    const form = useTenantDomainForm();
    const addProviderMutation = useAddSSOProvider();

    const handleSubmit = () => {
        addProviderMutation.mutate(
            {
                provider: 0,
                isBoth: form.showDomainRestriction,
                companyId: currentCompany,
                tenantIds: form.tenantIds,
                emailDomains: form.domains,
                isEnabled: false,
                isVerified: false,
            },
            { onSuccess: handleReset },
        );
    };

    const handleReset = () => {
        form.reset();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleReset} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader pb={0}>
                    <HStack align="start" justify="space-between">
                        <Stack spacing={3} flex={1}>
                            <Text fontWeight="bold" fontSize="xl">
                                Add Identity Provider — Microsoft
                            </Text>
                            <Stack spacing={2} align="flex-start">
                                <HStack spacing={3}>
                                    <MicrosoftProviderPreview
                                        borderStyle="dashed"
                                        borderWidth="1.5px"
                                    />
                                    <Badge
                                        colorScheme="messenger"
                                        borderRadius="full"
                                        size="0.75rem"
                                        px={3}
                                        py={1}
                                        fontWeight="semibold"
                                        textTransform="capitalize">
                                        Preview
                                    </Badge>
                                </HStack>
                                <Text
                                    fontSize="sm"
                                    fontStyle="italic"
                                    color="gray.500">
                                    This will enable using this button on the
                                    login page
                                </Text>
                            </Stack>
                        </Stack>
                        <IconButton
                            icon={<CloseIcon boxSize={3} />}
                            onClick={handleReset}
                            variant="ghost"
                            size="sm"
                            aria-label="Close"
                        />
                    </HStack>
                </ModalHeader>

                <ModalBody>
                    <TenantDomainFormBody
                        {...form}
                        darkMode={darkMode}
                        titlePrefix="sso"
                        columnFlex={1}
                        emptyGridHeight="100px"
                    />
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3} w="full" justify="flex-end">
                        <Button
                            data-walkthroughid={ufBackBtn}
                            variant="outline"
                            w={BUTTON_WIDTH}
                            onClick={handleReset}>
                            Cancel
                        </Button>
                        <Button
                            data-walkthroughid={ufSubmitBtn}
                            isLoading={addProviderMutation.isPending}
                            isDisabled={!form.isFormValid}
                            w={BUTTON_WIDTH}
                            onClick={handleSubmit}>
                            Add
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
